import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { useHackers } from "@/hooks/useHackers";
import { trackEvent } from "@/lib/analytics";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const HACKERS_PER_PAGE = 10;

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const hackersListSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  url: `${siteUrl}/hackers`,
  name: "Find and Hire a Professional Hacker — Hackers Lodge Cybersecurity Marketplace",
  description: "Browse verified freelance hackers specializing in penetration testing, ethical hacking, application security, network security, bug bounty, cloud security, digital forensics, and more. Filter by skill, country, and specialty.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

function StarRating({ rating }: { rating: number | null }) {
  const r = rating ?? 0;
  const fullStars = Math.floor(r);
  const hasHalf = r % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <i key={`full-${i}`} className="fas fa-star text-xs text-yellow-400" />
      ))}
      {hasHalf && (
        <i className="fas fa-star-half-alt text-xs text-yellow-400" />
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <i key={`empty-${i}`} className="far fa-star text-xs text-gray-300" />
      ))}
    </div>
  );
}

function HackerCardSkeleton() {
  return (
    <div className="bg-[#0d1117] border border-gray-700 rounded p-4 md:p-5 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-2/5" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
          <div className="h-6 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-800 flex gap-4">
        <div className="h-3 bg-gray-200 rounded w-16" />
        <div className="h-3 bg-gray-200 rounded w-14" />
        <div className="h-3 bg-gray-200 rounded w-20" />
      </div>
    </div>
  );
}

export default function HackersPage() {
  const { hackers, loading, error } = useHackers();
  const [searchQuery, setSearchQuery] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("All Specialties");
  const [countryFilter, setCountryFilter] = useState("All Countries");
  const [activePage, setActivePage] = useState(1);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fire GA4 secondary: service_page_view
  useEffect(() => {
    trackEvent("service_page_view", { page: "hackers_listing" });
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setActivePage(1);

    // Debounced search_expert event
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed.length >= 2) {
        trackEvent("search_expert", { query: trimmed });
      }
    }, 1000);
  };

  const allSpecialties = useMemo(() => {
    const set = new Set(hackers.map((h) => h.specialty));
    return ["All Specialties", ...Array.from(set).sort()];
  }, [hackers]);

  const allCountries = useMemo(() => {
    const set = new Set(hackers.map((h) => h.country));
    return ["All Countries", ...Array.from(set).sort()];
  }, [hackers]);

  const filtered = useMemo(() => {
    return hackers.filter((h) => {
      if (specialtyFilter !== "All Specialties" && h.specialty !== specialtyFilter) return false;
      if (countryFilter !== "All Countries" && h.country !== countryFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          h.name.toLowerCase().includes(q) ||
          h.specialty.toLowerCase().includes(q) ||
          (h.skills && h.skills.some((s: string) => s.toLowerCase().includes(q))) ||
          h.country.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [hackers, searchQuery, specialtyFilter, countryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / HACKERS_PER_PAGE));
  const startIndex = (activePage - 1) * HACKERS_PER_PAGE;
  const currentHackers = filtered.slice(startIndex, startIndex + HACKERS_PER_PAGE);

  const handlePageClick = (page: number) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (type: "specialty" | "country", value: string) => {
    if (type === "specialty") setSpecialtyFilter(value);
    else setCountryFilter(value);
    setActivePage(1);
  };

  const getPageNumbers = (): (number | "...")[] => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (activePage > 3) pages.push("...");
      const start = Math.max(2, activePage - 1);
      const end = Math.min(totalPages - 1, activePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (activePage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Find and Hire a Professional Hacker — Cybersecurity Marketplace"
        description="Browse verified freelance hackers specializing in penetration testing, ethical hacking, application security, network security, bug bounty, cloud security, digital forensics, and more. Filter by skill, country, and specialty."
        keywords="hire a hacker, professional hacker, freelance hacker, ethical hacking, penetration testing, cybersecurity marketplace, bug bounty, vulnerability assessment, cybersecurity experts, security consultant"
        canonicalUrl={`${siteUrl}/hackers`}
        ogType="website"
        schema={hackersListSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-8 md:py-12 bg-[#161b22] min-h-[80vh]">
          <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Hackers" }]} />
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                Find and Hire a Professional Hacker
              </h1>
              <p className="text-sm text-gray-500">
                Browse {hackers.length} verified hackers across {allCountries.length - 1} countries
                {filtered.length !== hackers.length && (
                  <span className="text-[#5bc0de] font-semibold"> — showing {filtered.length} results</span>
                )}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search hackers by name, skill, or specialty..."
                  className="w-full px-4 py-3 pr-10 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                />
                <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              <select
                value={specialtyFilter}
                onChange={(e) => handleFilterChange("specialty", e.target.value)}
                className="px-4 py-3 border border-gray-700 rounded text-sm text-gray-400 focus:outline-none focus:border-[#5bc0de]"
              >
                {allSpecialties.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <select
                value={countryFilter}
                onChange={(e) => handleFilterChange("country", e.target.value)}
                className="px-4 py-3 border border-gray-700 rounded text-sm text-gray-400 focus:outline-none focus:border-[#5bc0de]"
              >
                {allCountries.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <HackerCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <i className="fas fa-exclamation-triangle text-5xl text-yellow-400 mb-4 block" />
                <p className="text-gray-400 text-sm mb-4">Could not load hackers. Please try again.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap cursor-pointer"
                >
                  Retry
                </button>
              </div>
            ) : currentHackers.length === 0 ? (
              <div className="text-center py-16">
                <i className="fas fa-user-secret text-5xl text-gray-300 mb-4 block" />
                <p className="text-gray-500 text-sm">No hackers match your search. Try different filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentHackers.map((hacker) => (
                  <div
                    key={hacker.id}
                    className="bg-[#0d1117] border border-gray-700 rounded p-4 md:p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <img
                        src={hacker.avatar}
                        alt={hacker.name}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <Link
                              to={`/hacker/${hacker.id}`}
                              className="text-base font-semibold text-gray-200 hover:text-[#5bc0de] block truncate"
                            >
                              {hacker.verified && (
                                <i className="fas fa-check-circle text-[#5bc0de] text-xs mr-1" />
                              )}
                              {hacker.name}
                              {hacker.featured && (
                                <span className="ml-2 text-[10px] bg-[#f0ad4e]/20 text-[#f0ad4e] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">Featured</span>
                              )}
                            </Link>
                            <span className="text-xs text-[#5bc0de]">{hacker.specialty}</span>
                            <span className="text-xs text-gray-400 ml-2"><i className="fas fa-map-marker-alt mr-1" />{hacker.country}</span>
                            <span className="text-xs text-gray-400 ml-2">{hacker.gender === "male" ? "M" : "F"}, {hacker.age != null ? hacker.age : "—"}</span>
                          </div>
                          <Link
                            to={`/hacker/${hacker.id}`}
                            className="flex-shrink-0 inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold px-4 py-1.5 rounded transition-all whitespace-nowrap"
                          >
                            View Profile
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap items-center gap-4">
                      <StarRating rating={hacker.rating} />
                      <span className="text-sm text-gray-400">{hacker.rate}</span>
                      <span className="text-sm text-gray-500">{hacker.experience}</span>
                      {hacker.certifications && hacker.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 w-full">
                          {hacker.certifications.slice(0, 3).map((cert) => (
                            <span
                              key={cert}
                              className="text-[10px] bg-[#5bc0de]/10 text-[#5bc0de] px-2 py-0.5 rounded font-semibold uppercase tracking-wide"
                            >
                              <i className="fas fa-certificate mr-1" />{cert}
                            </span>
                          ))}
                          {hacker.certifications.length > 3 && (
                            <span className="text-[10px] text-gray-400 px-1 py-0.5 font-medium">
                              +{hacker.certifications.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      {hacker.skills && hacker.skills.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {hacker.skills.map((skill) => (
                            <span
                              key={skill}
                              className="text-xs bg-[#1c2128] text-gray-400 px-2 py-1 rounded"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !error && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => handlePageClick(Math.max(1, activePage - 1))}
                  disabled={activePage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="fas fa-angle-left" />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} className="text-sm text-gray-400 px-1">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageClick(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        activePage === p ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => handlePageClick(Math.min(totalPages, activePage + 1))}
                  disabled={activePage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="fas fa-angle-right" />
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}